import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Flag, EyeOff, Eye, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { reasonLabel } from '../data/reportReasons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '-');

const StatusBadge = ({ r }) => {
  const [txt, bg, fg] = r.book_deleted ? ['Livro excluído', '#edf2f7', '#4a5568'] : r.book_blocked ? ['Oculto pelo administrador', '#fed7d7', '#c53030'] : ['Visível no catálogo', '#c6f6d5', '#276749'];
  return <span data-testid={`report-status-${r.id}`} style={{ background: bg, color: fg, borderRadius: 50, padding: '0.15rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>{txt}</span>;
};

const STATUS = [['pendente', 'Pendente', '#c05621', '#feebc8'], ['em_analise', 'Em análise', '#2b6cb0', '#bee3f8'], ['resolvida', 'Resolvida', '#276749', '#c6f6d5']];

const ReportStatusEditor = ({ r, onSaved }) => {
  const [status, setStatus] = useState(r.status || 'pendente');
  const [note, setNote] = useState(r.admin_note || '');
  const [base, setBase] = useState({ status: r.status || 'pendente', note: r.admin_note || '' });
  const [saving, setSaving] = useState(false);
  const dirty = status !== base.status || note !== base.note;
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/reports/${r.id}`, { status, note }, { headers: authHeaders() });
      setBase({ status, note });
      onSaved();
    } catch (err) {
      alert(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Não foi possível salvar');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={{ borderTop: '1px dashed #e2e8f0', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {STATUS.map(([key, label, fg, bg]) => (
          <button key={key} type="button" onClick={() => setStatus(key)} data-testid={`report-set-status-${key}-${r.id}`}
            style={{ border: `1px solid ${status === key ? fg : '#e2e8f0'}`, background: status === key ? bg : 'white', color: status === key ? fg : '#718096',
              borderRadius: 50, padding: '0.2rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>{label}</button>
        ))}
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Nota interna (visível só para o admin)"
        data-testid={`report-note-input-${r.id}`} style={{ flex: 1, minWidth: 200, border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.35rem 0.6rem', fontSize: '0.85rem' }} />
      <Button size="sm" disabled={saving || !dirty} onClick={save} data-testid={`report-save-status-${r.id}`} style={{ background: '#0d9488', color: 'white' }}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : dirty ? 'Salvar' : 'Salvo'}
      </Button>
    </div>
  );
};

const ReportCard = ({ r, isAdmin, busy, onToggle, onSaved }) => (
  <div data-testid={`report-card-${r.id}`} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1rem 1.25rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div>
        <p style={{ fontWeight: 700, color: '#2D3748' }}>{r.book_titulo}{r.book_autor ? <span style={{ fontWeight: 400, color: '#718096' }}> — {r.book_autor}</span> : null}</p>
        <p style={{ fontSize: '0.8rem', color: '#718096' }}>{isAdmin && <>Editora: {r.editor_nome || '-'} · </>}{fmt(r.created_at)}</p>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {!isAdmin && !r.editor_read && <span data-testid={`report-new-${r.id}`} style={{ background: '#e53e3e', color: 'white', borderRadius: 50, padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: 700 }}>Nova</span>}
        <StatusBadge r={r} />
        {isAdmin && !r.book_deleted && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => onToggle(r)} data-testid={`report-toggle-book-${r.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : r.book_blocked ? <><Eye className="w-4 h-4 mr-1" />Exibir livro</> : <><EyeOff className="w-4 h-4 mr-1" />Ocultar livro</>}
          </Button>
        )}
      </div>
    </div>
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', margin: '0.75rem 0' }}>
      {r.reasons.map((id) => <span key={id} style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}>{reasonLabel(id)}</span>)}
    </div>
    {r.comment && <p data-testid={`report-comment-${r.id}`} style={{ fontSize: '0.9rem', color: '#2D3748', whiteSpace: 'pre-wrap', marginBottom: '0.5rem' }}>{r.comment}</p>}
    <p style={{ fontSize: '0.78rem', color: '#718096' }}>Denunciado por: {r.reporter_nome || '-'} ({r.reporter_type === 'editor' ? 'editor' : 'leitor'}) · {r.reporter_email}</p>
    {isAdmin && <ReportStatusEditor r={r} onSaved={onSaved} />}
  </div>
);

export const ReportsList = ({ isAdmin, onCountChange }) => {
  const [reports, setReports] = useState(null);
  const [busyBook, setBusyBook] = useState(null);
  const unseenIds = useRef(new Set());
  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/${isAdmin ? 'admin' : 'digital'}/reports`, { headers: authHeaders() });
    data.reports.forEach((x) => { if (!isAdmin && !x.editor_read) unseenIds.current.add(x.id); });
    setReports(data.reports.map((x) => (isAdmin ? x : { ...x, editor_read: !unseenIds.current.has(x.id) })));
    if (!isAdmin && data.reports.some((x) => !x.editor_read)) {
      await axios.post(`${API}/digital/reports/mark-read`, {}, { headers: authHeaders() });
    }
    if (!isAdmin) onCountChange?.(0);
  }, [isAdmin]);
  useEffect(() => { load().catch(() => setReports([])); }, [load]);

  const toggle = async (r) => {
    setBusyBook(r.book_id);
    try {
      await axios.put(`${API}/admin/books/${r.book_id}/block`, { blocked: !r.book_blocked }, { headers: authHeaders() });
      await load();
    } catch (err) {
      alert(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Não foi possível alterar o livro');
    } finally {
      setBusyBook(null);
    }
  };

  return (
    <div data-testid="reports-page">
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Flag className="w-6 h-6" style={{ color: '#e53e3e' }} /> Denúncias
      </h1>
      <p style={{ color: '#718096', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        {isAdmin ? 'Denúncias enviadas pelos usuários. Você pode ocultar o livro do catálogo da editora e exibi-lo novamente.' : 'Denúncias recebidas sobre livros do seu catálogo. Apenas o administrador pode ocultar ou reexibir um livro denunciado.'}
      </p>
      {reports === null ? <Loader2 className="w-6 h-6 animate-spin" /> : reports.length === 0 ? (
        <p data-testid="reports-empty" style={{ color: '#718096' }}>Nenhuma denúncia recebida.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {reports.map((r) => <ReportCard key={r.id} r={r} isAdmin={isAdmin} busy={busyBook === r.book_id} onToggle={toggle} onSaved={() => onCountChange?.()} />)}
        </div>
      )}
    </div>
  );
};
